import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import * as reportesRecepcionService from '../../services/reportes/reportesRecepcionService';
import { listarTiposTurno } from '../../services/tipoTurnoService';
import { usePDFReportes } from '../../components/ReportesPDF';
import { useExcelReportes } from '../../components/ReportesExcel';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { COLOR, FUENTE } from './reportes/tokens';
import { usePeriodoReportes } from './reportes/periodoReportes';
import BarraPeriodo from './reportes/BarraPeriodo';
import VolumenPorTipo from './reportes/VolumenPorTipo';
import ResultadoAtencionChart from './reportes/ResultadoAtencionChart';
import MotivosNoAvance from './reportes/MotivosNoAvance';
import {
    BotonSecundario, EncabezadoReportes, SelectFiltro, TIPO_EXCLUIDO_DE_ESTADISTICAS, TIPOS_TRAMITE_FALLBACK,
} from './reportes/ComponentesReportes';
import NavegacionReportesRecepcion from './reportes/NavegacionReportesRecepcion';

// Rangos de esta vista: el backfill de la planilla va de mayo a agosto de 2026, así que arranca en
// el año en curso (con "Mes" casi todo quedaría afuera). "Todo" = sin fechas.
const RANGOS_HISTORICAS = [
    { id: 'mes', label: 'Mes' },
    { id: 'anio', label: 'Año' },
    { id: 'rango', label: 'Rango' },
    { id: 'todo', label: 'Todo' },
];

/**
 * Estadísticas históricas: los tres reportes que suman las atenciones de la planilla manual
 * (mayo a agosto de 2026) además de los turnos del sistema. Antes era una pestaña más de
 * Reportes Recepción; ahora es una vista propia a la que se llega desde la vista simple y desde
 * Estadísticas Pro.
 *
 * No se manda `area`: el backfill no tiene área de atención y el service descarta el histórico
 * cuando viene un área (ver el `if (area == null)` en ReportesRecepcionService).
 */
function ReportesRecepcionHistoricas() {
    const periodo = usePeriodoReportes('anio', RANGOS_HISTORICAS);
    const { desde, hasta, vacio } = periodo;

    const [filtroTipoTramite, setFiltroTipoTramite] = useState('');
    const [tiposTramite, setTiposTramite] = useState(TIPOS_TRAMITE_FALLBACK);
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        listarTiposTurno(true)
            .then((lista) => {
                // Solo los tipos que entran en las estadísticas (V237): filtrar por uno excluido daría vacío.
                const codigos = (lista || [])
                    .filter((t) => t?.cuentaEnEstadisticas !== false)
                    .map((t) => t?.codigo).filter((c) => c && c !== TIPO_EXCLUIDO_DE_ESTADISTICAS);
                if (codigos.length) setTiposTramite(codigos);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        let vigente = true;
        if (vacio) {
            setDatos({ porTipo: [], resultadoAtencion: [], motivosNoAvance: [] });
            setCargando(false);
            return () => { vigente = false; };
        }
        setCargando(true);
        // Es la única pantalla que suma la planilla manual a los turnos del sistema.
        const filtros = {
            fechaDesde: desde || null, fechaHasta: hasta || null, tipoTramite: filtroTipoTramite || null, area: null,
            incluirPlanilla: true,
        };
        Promise.all([
            reportesRecepcionService.getPorTipo(filtros),
            reportesRecepcionService.getResultadoAtencion(filtros),
            reportesRecepcionService.getMotivosNoAvance(filtros),
        ])
            .then(([porTipo, resultadoAtencion, motivosNoAvance]) => {
                if (!vigente) return;
                setDatos({
                    porTipo: Array.isArray(porTipo) ? porTipo : [],
                    resultadoAtencion: Array.isArray(resultadoAtencion) ? resultadoAtencion : [],
                    motivosNoAvance: Array.isArray(motivosNoAvance) ? motivosNoAvance : [],
                });
            })
            .catch((err) => {
                console.error('Error cargando las estadísticas históricas:', err);
                if (vigente) setDatos(null);
            })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [desde, hasta, vacio, filtroTipoTramite]);

    const { generarYDescargar } = usePDFReportes();
    const { generarYDescargar: generarExcelYDescargar } = useExcelReportes();

    const configExportacion = () => ({
        tipo: 'RECEPCION',
        vista: 'historicas',
        seccionExportar: 'historicas',
        historicas: datos,
        rangoHistoricas: periodo.etiqueta,
        formatearTipo: formatearTipoTramite,
        filtros: {
            periodo: periodo.etiqueta,
            tipoTramite: filtroTipoTramite ? formatearTipoTramite(filtroTipoTramite) : 'Todos',
            area: 'No aplica (las atenciones históricas no tienen área)',
            canal: 'Todos',
        },
    });

    return (
        <Box className="mob-fondo-completo area-recepcion" sx={{ background: '#F4F5F7', minHeight: '100vh', margin: { xs: '-0.75rem', md: '-1.5rem' }, padding: { xs: '0.75rem', md: '1.5rem' } }}>
            <EncabezadoReportes
                migas={[{ label: 'Reportes', to: '/reportes' }, { label: 'Recepción', to: '/reportes/recepcion' }, { label: 'Estadísticas históricas' }]}
                titulo="Estadísticas históricas"
                subtitulo="Turnos del sistema más las atenciones de la planilla manual (mayo a agosto de 2026)."
                acciones={(
                    <>
                        <BotonSecundario onClick={() => generarExcelYDescargar(configExportacion(), 'estadisticas_historicas_recepcion')} disabled={!datos}>Exportar Excel</BotonSecundario>
                        <BotonSecundario primario onClick={() => generarYDescargar(configExportacion())} disabled={!datos}>Exportar PDF</BotonSecundario>
                    </>
                )}
            />

            <NavegacionReportesRecepcion activa="historicas" />

            <BarraPeriodo periodo={periodo} rangos={RANGOS_HISTORICAS} />

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
                <SelectFiltro
                    label="Tipo de trámite" valor={filtroTipoTramite} onChange={setFiltroTipoTramite}
                    opciones={tiposTramite.map((t) => ({ valor: t, etiqueta: formatearTipoTramite(t) }))}
                />
            </Box>

            {cargando ? (
                <Skeleton variant="rounded" height={320} />
            ) : !datos ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No se pudieron cargar las estadísticas históricas.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                    <VolumenPorTipo
                        data={datos.porTipo}
                        subtitulo="Turnos del sistema más las atenciones de la planilla, sumados por tipo."
                    />
                    <ResultadoAtencionChart data={datos.resultadoAtencion} />
                    <MotivosNoAvance data={datos.motivosNoAvance} />
                </Box>
            )}
        </Box>
    );
}

export default ReportesRecepcionHistoricas;
